import { useEffect, useRef, useState } from 'react';

const GOLD = {
  50: '#FBF6E3', 100: '#F5E9B8', 300: '#E4CC6C', 400: '#D6B84A',
  500: '#C9A234', 600: '#B58A1E', 700: '#8F6C16',
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const easeOutCubic = (x) => 1 - Math.pow(1 - x, 3);
const easeInOutQuart = (x) => (x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2);
const easeInCubic = (x) => x * x * x;

const DURATION = 3.8;

export default function LogoIntro({ onDone }) {
  const [t, setT] = useState(0);
  const rafRef = useRef(0);
  const startRef = useRef(0);
  const [skipped, setSkipped] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { onDone?.(); return; }

    startRef.current = performance.now();
    const tick = (now) => {
      const elapsed = (now - startRef.current) / 1000;
      setT(elapsed);
      if (elapsed >= DURATION) {
        onDone?.();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [onDone]);

  useEffect(() => {
    const skip = () => { if (!skipped) { setSkipped(true); onDone?.(); } };
    window.addEventListener('keydown', skip);
    window.addEventListener('click', skip);
    return () => {
      window.removeEventListener('keydown', skip);
      window.removeEventListener('click', skip);
    };
  }, [skipped, onDone]);

  const curtainP = easeInOutQuart(clamp((t - 0.6) / 0.9, 0, 1));
  const curtainOffset = curtainP * 52;
  const gridP = easeOutCubic(clamp((t - 0.1) / 0.7, 0, 1));
  const gridOut = easeInCubic(clamp((t - 3.2) / 0.5, 0, 1));
  const gridOpacity = 1 - gridOut;
  const logoP = easeOutCubic(clamp((t - 1.1) / 0.8, 0, 1));
  const logoScale = 0.85 + logoP * 0.15;
  const logoTilt = (1 - logoP) * -6;
  const shim = clamp((t - 2.0) / 0.8, 0, 1);
  const pulse = 25 + (Math.sin((t - 1.2) * 1.4) * 0.5 + 0.5) * 18;
  const exit = easeInCubic(clamp((t - 3.3) / 0.5, 0, 1));
  const logoOpacity = logoP * (1 - exit);
  const logoExitScale = 1 + exit * 0.2;
  const logoBlur = exit * 5;
  const pageFade = easeInCubic(clamp((t - 3.4) / 0.4, 0, 1));
  const progress = clamp(t / 3.3, 0, 1);
  const progressOpacity = easeOutCubic(clamp(t / 0.5, 0, 1)) * (1 - easeInCubic(clamp((t - 3.1) / 0.3, 0, 1)));
  const metaIn = easeOutCubic(clamp((t - 1.6) / 0.5, 0, 1));
  const metaOut = easeInCubic(clamp((t - 3.2) / 0.4, 0, 1));
  const metaOpacity = metaIn * (1 - metaOut);

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden"
      style={{ background: '#060607', opacity: 1 - pageFade, pointerEvents: pageFade > 0.9 ? 'none' : 'auto' }}
    >
      {/* Spotlight + vignette */}
      <div className="absolute inset-0" style={{
        background: `radial-gradient(ellipse 50% 40% at 50% 50%, rgba(201,162,52,0.18) 0%, transparent 60%)`,
      }} />
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)',
      }} />

      {/* Precision grid / corner ticks */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice" style={{ opacity: gridOpacity }}>
        <line x1={960 - gridP * 912} y1={540} x2={960 + gridP * 912} y2={540} stroke={GOLD[500]} strokeWidth="1" opacity="0.35" />
        <line x1={960} y1={540 - gridP * 459} x2={960} y2={540 + gridP * 459} stroke={GOLD[500]} strokeWidth="1" opacity="0.25" />
        {[[580, 320], [1340, 320], [580, 760], [1340, 760]].map(([x, y], i) => {
          const len = 20 * gridP;
          const dx = i % 2 === 0 ? 1 : -1;
          const dy = i < 2 ? 1 : -1;
          return (
            <g key={i} opacity={gridP}>
              <line x1={x} y1={y} x2={x + dx * len} y2={y} stroke={GOLD[500]} strokeWidth="1.5" />
              <line x1={x} y1={y} x2={x} y2={y + dy * len} stroke={GOLD[500]} strokeWidth="1.5" />
            </g>
          );
        })}
      </svg>

      {/* Corner meta */}
      <div className="absolute text-[11px] tracking-[0.15em] uppercase" style={{
        top: 40, left: 60, color: GOLD[400], opacity: metaOpacity * 0.8,
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
      }}>
        <span style={{ display: 'inline-block', width: 18, height: 1, background: GOLD[500], marginRight: 10, verticalAlign: 'middle' }} />
        MODERN MOTORS / 01
      </div>
      <div className="absolute text-[11px] tracking-[0.15em] uppercase text-right" style={{
        top: 40, right: 60, color: GOLD[400], opacity: metaOpacity * 0.8,
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
      }}>
        EMPLOYEE PORTAL
        <span style={{ display: 'inline-block', width: 18, height: 1, background: GOLD[500], marginLeft: 10, verticalAlign: 'middle' }} />
      </div>
      <div className="absolute text-[11px] tracking-[0.15em] uppercase" style={{
        bottom: 40, left: 60, color: GOLD[400], opacity: metaOpacity * 0.8,
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
      }}>
        <span style={{ display: 'inline-block', width: 18, height: 1, background: GOLD[500], marginRight: 10, verticalAlign: 'middle' }} />
        INDEX · AUTHENTICATE
      </div>
      <div className="absolute text-[11px] tracking-[0.15em] uppercase text-right" style={{
        bottom: 40, right: 60, color: GOLD[400], opacity: metaOpacity * 0.8,
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
      }}>
        v2026.04
        <span style={{ display: 'inline-block', width: 18, height: 1, background: GOLD[500], marginLeft: 10, verticalAlign: 'middle' }} />
      </div>

      {/* Logo reveal */}
      <div className="absolute inset-0 flex items-center justify-center" style={{
        opacity: logoOpacity, filter: logoBlur ? `blur(${logoBlur}px)` : 'none',
      }}>
        <div
          className="relative"
          style={{
            width: 'min(640px, 70vw)',
            aspectRatio: '2 / 1',
            transform: `scale(${logoScale * logoExitScale}) perspective(1200px) rotateX(${logoTilt}deg)`,
            filter: `drop-shadow(0 0 ${pulse * (1 - exit)}px rgba(201,162,52,0.5))`,
            willChange: 'transform, filter',
          }}
        >
          <img
            src="/logo-modernmotors.png"
            alt="Modern Motors"
            className="w-full h-full object-contain block select-none"
            draggable={false}
          />
          {shim > 0 && shim < 1 && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ mixBlendMode: 'overlay' }}>
              <div style={{
                position: 'absolute', top: '-20%', bottom: '-20%',
                left: `${-30 + shim * 160}%`, width: '22%',
                background: 'linear-gradient(100deg, transparent 0%, rgba(255,255,255,0.95) 50%, transparent 100%)',
                transform: 'skewX(-18deg)',
                opacity: Math.sin(shim * Math.PI),
              }} />
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="absolute" style={{
        left: '15%', right: '15%', bottom: 120, height: 1,
        background: 'rgba(201,162,52,0.15)', opacity: progressOpacity,
      }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: `${progress * 100}%`,
          background: `linear-gradient(90deg, ${GOLD[700]}, ${GOLD[400]}, ${GOLD[50]})`,
        }} />
        <div style={{
          position: 'absolute', left: `${progress * 100}%`, top: '50%',
          width: 6, height: 6, marginLeft: -3, marginTop: -3,
          background: GOLD[50], borderRadius: '50%',
          boxShadow: `0 0 10px ${GOLD[400]}`,
        }} />
      </div>

      {/* Split curtain — on top, slides apart */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 3 }}>
        <div style={{
          position: 'absolute', top: 0, bottom: 0, left: 0, width: '50%',
          background: '#060607',
          transform: `translateX(-${curtainOffset}%)`,
          borderRight: `1px solid ${GOLD[500]}`,
        }} />
        <div style={{
          position: 'absolute', top: 0, bottom: 0, right: 0, width: '50%',
          background: '#060607',
          transform: `translateX(${curtainOffset}%)`,
          borderLeft: `1px solid ${GOLD[500]}`,
        }} />
      </div>

      {/* Skip hint */}
      <button
        onClick={() => { setSkipped(true); onDone?.(); }}
        className="absolute bottom-4 right-4 text-[10px] tracking-[0.15em] uppercase px-2 py-1 rounded border"
        style={{
          color: GOLD[400], borderColor: `${GOLD[700]}88`, zIndex: 10,
          background: 'rgba(0,0,0,0.4)', opacity: metaOpacity * 0.7,
          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        }}
      >
        Skip ›
      </button>
    </div>
  );
}
